import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { ApiResponse } from '@core/models/common.model';
import { AuthStorageService } from '@auth/services/auth-storage.service';
import { AppSettings, DEFAULT_SETTINGS, GridSettings, ThemeType, } from '@core/models/settings.model';
import { applyThemeClass, DEFAULT_THEME, AppTheme } from '@theme/themes';
import { GenericTableColumn } from '@shared/models/generic-table.model';

const STORAGE_KEY = 'appSettings';

@Injectable({ providedIn: 'root' })
export class SettingsContextService {
  private readonly settingsSubject = new BehaviorSubject<AppSettings>(this.loadSettings());
  readonly settings$ = this.settingsSubject.asObservable();
  readonly patientColumns$ = this.settings$.pipe(map(settings => settings.patientColumns));
  
  private readonly endpoint = `${environment.apiBaseUrl}/UserSettings`;
  /** The user whose saved settings were last loaded from the server. */
  private loadedFor: string | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly http: HttpClient,
    private readonly authStorage: AuthStorageService,
  ) { this.applyTheme(this.theme); }

  /**
   * Replaces the settings with the ones saved for the signed-in user on the server, once per user, so they follow the user to any device.
   * With nothing saved yet, the choices already made in this browser are sent up as the starting point.
   */
  loadForCurrentUser(): void {
    const userId = this.authStorage.getUser()?.id;
    if (!userId || !this.authStorage.isTokenValid() || this.loadedFor === userId) return;
    this.loadedFor = userId;

    this.http.get<ApiResponse<{ settings: string | null }>>(this.endpoint).subscribe({
      next: (res) => {
        const saved = res.data?.settings;
        if (saved) {
          try {
            this.updateSettings({ ...structuredClone(DEFAULT_SETTINGS), ...JSON.parse(saved) });
            this.saveToStorage();
            this.applyTheme(this.theme);
          } catch {
            /* unreadable settings on the server: keep what is here */
          }
        } else if (localStorage.getItem(STORAGE_KEY)) {
          this.saveSettings().subscribe();
        }
      },
      error: () => (this.loadedFor = null), // try again on the next navigation
    });
  }

  /** Signing out: the next person on this browser starts from their own settings, not these. */
  clearOnSignOut(): void {
    this.loadedFor = null;
    localStorage.removeItem(STORAGE_KEY);
    this.updateSettings(structuredClone(DEFAULT_SETTINGS));
    this.applyTheme(this.theme);
  }

  get settings(): AppSettings {
    return this.settingsSubject.value;
  }

  get patientColumns(): GenericTableColumn[] {
    return this.settings.patientColumns;
  }
  get appointmentColumns(): GenericTableColumn[] {
    return this.settings.appointmentColumns;
  }

  get medicalRecordColumns(): GenericTableColumn[] {
    return this.settings.medicalRecordColumns;
  }

  get gridSettings(): GridSettings {
    return this.settings.gridSettings;
  }

  get theme(): ThemeType {
    return this.settings.theme;
  }

  get reducedMotion(): boolean {
    return this.settings.reducedMotion;
  }

  get highContrast(): boolean {
    return this.settings.highContrast;
  }
  private applyTheme(theme: ThemeType): void {
    applyThemeClass(this.document.body, this.getThemeClass(theme));
  }

  private getThemeClass(theme: ThemeType): AppTheme {
    return theme === 'blue' ? 'theme-blue' : DEFAULT_THEME;
  }
  private updateSettings(settings: AppSettings): void {
    this.settingsSubject.next(settings);
  }

  private saveToStorage(): void {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(this.settingsSubject.value)
    );
  }

  private loadSettings(): AppSettings {

    const saved = localStorage.getItem(STORAGE_KEY);

    if (!saved) {
      return structuredClone(DEFAULT_SETTINGS);
    }

    try {
      return {
        ...structuredClone(DEFAULT_SETTINGS),
        ...JSON.parse(saved),
      };
    } catch {
      return structuredClone(DEFAULT_SETTINGS);
    }
  }

  updatePatientColumns(columns: GenericTableColumn[]): void {
    
    this.updateSettings({
      ...this.settings,
      patientColumns: columns,
    });
    this.saveToStorage();
  }

  updateTheme(theme: ThemeType): void {
    this.updateSettings({
      ...this.settings,
      theme,
    });
    this.saveToStorage();
    this.applyTheme(theme);
  }

  updateReducedMotion(value: boolean): void {
    this.updateSettings({
      ...this.settings,
      reducedMotion: value,
    });

    this.saveToStorage();
  }

  updateHighContrast(value: boolean): void {
    this.updateSettings({
      ...this.settings,
      highContrast: value,
    });

    this.saveToStorage();
  }

  updateGridSettings(settings: Partial<GridSettings>): void {
    this.updateSettings({
      ...this.settings,
      gridSettings: {
        ...this.settings.gridSettings,
        ...settings,
      },
    });

    this.saveToStorage();
  }

  /** Keeps the settings in this browser and on the server (the signed-in user's). */
  saveSettings(): Observable<unknown> {
    this.saveToStorage();
    return this.http.put(this.endpoint, { settings: JSON.stringify(this.settingsSubject.value) });
  }

  resetSettings(): void {
    localStorage.removeItem(STORAGE_KEY);

    this.updateSettings(structuredClone(DEFAULT_SETTINGS));

    this.saveToStorage();
    this.applyTheme(this.theme);
  }

  updateAppointmentColumns(columns: GenericTableColumn[]): void {
    this.updateSettings({
      ...this.settings,
      appointmentColumns: columns,
    });

    this.saveToStorage();
  }

  updateMedicalRecordColumns(columns: GenericTableColumn[]): void {
    this.updateSettings({
      ...this.settings,
      medicalRecordColumns: columns,
    });

    this.saveToStorage();
  }

  toggleColumnVisibility(
    columnId: string,
    gridType: 'patients' | 'appointments' | 'medicalRecords'
  ): void {

    const columns =
      gridType === 'patients'
        ? [...this.patientColumns]
        : gridType === 'appointments'
        ? [...this.appointmentColumns]
        : [...this.medicalRecordColumns];

    const updated = columns.map(column =>
      column.id === columnId
        ? {
            ...column,
            visible: !column.visible,
          }
        : column
    );

    this.updateColumns(gridType, updated);
  }

  updateColumnLabel(
    columnId: string,
    newLabel: string,
    gridType: 'patients' | 'appointments' | 'medicalRecords'
  ): void {

    const columns =
      gridType === 'patients'
        ? [...this.patientColumns]
        : gridType === 'appointments'
        ? [...this.appointmentColumns]
        : [...this.medicalRecordColumns];

    const updated = columns.map(column =>
      column.id === columnId
        ? {
            ...column,
            label: newLabel,
          }
        : column
    );

    this.updateColumns(gridType, updated);
  }

  moveColumnUp(
    columnId: string,
    gridType: 'patients' | 'appointments' | 'medicalRecords'
  ): void {

    const columns =
      gridType === 'patients'
        ? [...this.patientColumns]
        : gridType === 'appointments'
        ? [...this.appointmentColumns]
        : [...this.medicalRecordColumns];

    const index = columns.findIndex(c => c.id === columnId);

    if (index <= 0) {
      return;
    }

    const temp = columns[index].order ?? 0;
    columns[index].order = columns[index - 1].order ?? 0;
    columns[index - 1].order = temp;
    columns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    this.updateColumns(gridType, columns);
  }

  moveColumnDown(
    columnId: string,
    gridType: 'patients' | 'appointments' | 'medicalRecords'
  ): void {

    const columns =
      gridType === 'patients'
        ? [...this.patientColumns]
        : gridType === 'appointments'
        ? [...this.appointmentColumns]
        : [...this.medicalRecordColumns];

    const index = columns.findIndex(c => c.id === columnId);

    if (index >= columns.length - 1) {
      return;
    }

// Ensure order values are defined
    const temp = columns[index].order ?? 0;
    columns[index].order = columns[index + 1].order ?? 0;
    columns[index + 1].order = temp;
    columns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    this.updateColumns(gridType, columns);
  }

  private updateColumns(
    gridType: 'patients' | 'appointments' | 'medicalRecords',
    columns: GenericTableColumn[]
  ): void {

    switch (gridType) {

      case 'patients':
        this.updatePatientColumns(columns);
        break;

      case 'appointments':
        this.updateAppointmentColumns(columns);
        break;

      case 'medicalRecords':
        this.updateMedicalRecordColumns(columns);
        break;
    }
  }

}
